import { Card, Empty, Spin, Tag, Timeline, Typography } from "antd";
import React from "react";
import { IPartNumberHistoryResponseDto } from "../../models/History";
import DirectIcon from "../icons/DirectIcon";

export interface IHistoryTimelineProps {
  id?: number;
  title?: string;
  data: IPartNumberHistoryResponseDto["history"];
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
}

// Helper functions để label các hành động
const getActionLabel = (action: string): string => {
  switch (action) {
    case "IMPORT":
      return "Nhập kho";
    case "EXPORT":
      return "Xuất kho";
    case "EXPORT_TRANSFER":
      return "Xuất luân chuyển kho";
    case "NG_MARKED":
      return "Đánh dấu NG";
    case "QC_PASS":
      return "Kiểm tra đạt";
    case "QC_FAIL":
      return "Kiểm tra không đạt";
    case "RESTOCK":
      return "Nhập lại kho";
    case "STOCK_ADJUSTMENT":
      return "Điều chỉnh tồn kho";
    default:
      return action;
  }
};

const getReferenceLabel = (referenceType: string): string => {
  switch (referenceType) {
    case "IMPORT":
      return "Nhập kho";
    case "EXPORT":
      return "Xuất kho";
    case "QC":
      return "Kiểm tra chất lượng";
    default:
      return referenceType;
  }
};

const getColorByReferenceType = (referenceType: string): string => {
  switch (referenceType) {
    case "IMPORT":
      return "green";
    case "EXPORT":
      return "orange";
    default:
      return "blue";
  }
};
const getLinkByAction = (action: string, referenceId: number): string => {
  switch (action) {
    case "IMPORT":
      return `/warehouse/list-ex-im?tab=import&importId=${referenceId}`;
    case "EXPORT":
      return `/warehouse/list-ex-im?tab=export&exportId=${referenceId}`;
    case "EXPORT_TRANSFER":
      return `/warehouse/list-ex-im?tab=transfer&transferId=${referenceId}`;
    case "QC_FAIL":
      return `/quality-control/qc-issues?qcResultId=${referenceId}`;
    case "QC_PASS":
      return `/quality-control/qc-issues?qcResultId=${referenceId}`;
    default:
      return "";
  }
};

const HistoryTimeline: React.FC<IHistoryTimelineProps> = ({
  title = "Lịch sử",
  data,
  isLoading = false,
  isEmpty = false,
  emptyMessage = "Không có lịch sử",
}) => {
  return (
    <Card
      title={
        <Typography.Title level={5} className="m-0">
          {title}
        </Typography.Title>
      }
      className="h-[calc(100vh-126px)] overflow-auto"
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : isEmpty || data.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <Empty
            description={emptyMessage}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <Timeline
          mode="left"
          items={data.map((item) => ({
            children: (
              <div className="pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag color={getColorByReferenceType(item.referenceType)}>
                    {getReferenceLabel(item.referenceType)}
                  </Tag>
                  <Typography.Text type="secondary" className="text-sm">
                    {new Date(item.actionDate).toLocaleString("vi-VN")}
                  </Typography.Text>
                </div>

                <div className="flex flex-col gap-2 p-3 rounded-md">
                  <div className="flex items-center gap-2">
                    <Typography.Text strong>
                      {getActionLabel(item.action)}
                    </Typography.Text>
                    <div>
                      <DirectIcon
                        title="Xem thông tin"
                        className="hover:text-blue-500"
                        onClick={() => {
                          window.open(
                            getLinkByAction(item.action, item.referenceId),
                            "_blank",
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Typography.Text type="secondary">
                        Số lượng thay đổi:
                      </Typography.Text>
                      <Tag
                        color={item.quantityChanged > 0 ? "green" : "orange"}
                      >
                        {item.quantityChanged > 0 ? "+" : ""}
                        {item.quantityChanged}
                      </Tag>
                    </div>
                    <div className="flex items-center gap-1">
                      <Typography.Text type="secondary">
                        Người duyệt:
                      </Typography.Text>
                      <Typography.Text>{item.user.fullName}</Typography.Text>
                    </div>
                    {item.location && (
                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">
                          Vị trí:
                        </Typography.Text>
                        <Typography.Text>{item.location.code}</Typography.Text>
                      </div>
                    )}
                    {item.note && (
                      <div className="flex items-center gap-1">
                        <Typography.Text type="secondary">
                          Ghi chú:
                        </Typography.Text>
                        <Typography.Text>{item.note}</Typography.Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ),
            color: getColorByReferenceType(item.referenceType),
          }))}
        />
      )}
    </Card>
  );
};

export default HistoryTimeline;
